import React, { useState, useEffect, useRef } from "react";
import clsx from "clsx";

export interface StoreOption {
  id: string;
  name: string;
  isActive?: boolean;
  count?: number;
  marketplace?: string;
}

export interface StoreFilterProps {
  stores: StoreOption[];
  selectedStores: string[];
  onSelectionChange: (selectedStoreIds: string[]) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  showCounts?: boolean; // Enable/disable count display (NOTE: showCounts=true not fully supported yet)
  showSelectionStyle?: boolean; // Enable selection state styling
  autoFocus?: boolean; // Auto-focus search input
  maxDisplayItems?: number; // Maximum items to show before showing count
  testId?: string; // Custom test ID for multiple instances
}

function StoreFilter({
  stores,
  selectedStores,
  onSelectionChange,
  placeholder = "All Stores",
  className = "",
  disabled = false,
  showCounts = false,
  showSelectionStyle = false,
  autoFocus = false,
  maxDisplayItems = 2,
  testId = "store-filter-dropdown",
}: StoreFilterProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [tempSelectedStores, setTempSelectedStores] = useState<string[]>([]);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Initialize temp selection when dropdown opens
  useEffect(() => {
    if (isOpen) {
      setTempSelectedStores([...selectedStores]);
    }
  }, [isOpen, selectedStores]);

  // Close dropdown when clicking outside or scrolling externally
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    function handleScroll(event: Event) {
      if (!isOpen) return;

      // Only close dropdown if scroll is external (not within dropdown)
      const target = event.target;
      const dropdownElement = dropdownRef.current;

      // Check if target is an Element (can be Document/Window in some cases)
      if (target instanceof Element && dropdownElement) {
        // Check if scroll is happening within the dropdown or its children
        if (
          dropdownElement.contains(target) ||
          target.closest('[data-testid="store-filter-dropdown"]') ||
          target.closest('[data-testid="store-filter-select-dropdown"]')
        ) {
          // This is internal dropdown scrolling, don't close
          return;
        }
      }

      // This is external scrolling (page scroll), close the dropdown
      setIsOpen(false);
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      // Listen for scroll events on window and document to catch external scrolling
      window.addEventListener("scroll", handleScroll, true);
      document.addEventListener("scroll", handleScroll, true);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScroll, true);
      document.removeEventListener("scroll", handleScroll, true);
    };
  }, [isOpen]);

  // Focus management for search input
  useEffect(() => {
    if (isOpen && autoFocus && searchInputRef.current) {
      // Use a small delay to ensure the dropdown is rendered
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [isOpen, autoFocus]);

  // Escape key handling
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen]);

  // Filter stores based on search term
  const filteredStores = stores.filter((store) =>
    store.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Check if all stores are selected (use temp selection when dropdown is open)
  const currentSelection = isOpen ? tempSelectedStores : selectedStores;
  const allStoresSelected =
    stores.length > 0 && currentSelection.length === stores.length;

  // Format store display name with optional count
  const formatStoreDisplayName = (store: StoreOption) => {
    if (showCounts && typeof store.count === "number") {
      return `${store.name} (${store.count.toLocaleString()})`;
    }
    return store.name;
  };

  // Get marketplace logo info (path or letter badge)
  const getMarketplaceInfo = (store: StoreOption) => {
    // Get marketplace type from store.marketplace field
    const marketplaceType = store.marketplace || "unknown";

    const logoMap: Record<string, string> = {
      shopee: "/media/logos/marketplace/shopee.png",
      lazada: "/media/logos/marketplace/lazada.png",
      tiktok: "/media/logos/marketplace/tiktok_shop.png",
      tiktokshop: "/media/logos/marketplace/tiktok_shop.png",
      tokopedia: "/media/logos/marketplace/tokopedia.png",
      blibli: "/media/logos/marketplace/blibli.png",
      bukalapak: "/media/logos/marketplace/bukalapak.png",
      jdid: "/media/logos/marketplace/jdid.png",
      bhinneka: "/media/logos/marketplace/bhinneka.png",
      orami: "/media/logos/marketplace/orami.png",
    };

    const logoPath = logoMap[marketplaceType.toLowerCase()];

    if (logoPath) {
      return { type: "image", src: logoPath };
    } else {
      // Fallback to letter badge
      const letter = store.name.charAt(0).toUpperCase();
      const bgColor = getLetterBadgeColor(marketplaceType);
      return { type: "letter", letter, bgColor };
    }
  };

  // Get color for letter badge based on marketplace type
  const getLetterBadgeColor = (marketplace: string) => {
    const normalized = marketplace?.toLowerCase();
    if (normalized?.includes("shopee")) return "bg-orange-500";
    if (normalized?.includes("lazada")) return "bg-blue-600";
    if (normalized?.includes("tiktok")) return "bg-black";
    if (normalized?.includes("tokopedia")) return "bg-green-500";
    return "bg-gray-500"; // default
  };

  const handleToggleDropdown = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const handleAllStoresToggle = () => {
    let newSelection: string[];
    if (allStoresSelected) {
      // Deselect all stores
      newSelection = [];
    } else {
      // Select all stores
      newSelection = stores.map((store) => store.id);
    }
    setTempSelectedStores(newSelection);
    // Don't call onSelectionChange immediately - wait for Apply button
  };

  const handleStoreToggle = (storeId: string) => {
    let newSelection: string[];
    if (tempSelectedStores.includes(storeId)) {
      // Remove store from selection
      newSelection = tempSelectedStores.filter((id) => id !== storeId);
    } else {
      // Add store to selection
      newSelection = [...tempSelectedStores, storeId];
    }
    setTempSelectedStores(newSelection);
    // Don't call onSelectionChange immediately - wait for Apply button
  };

  const handleClearAll = () => {
    setTempSelectedStores([]);
    // Don't call onSelectionChange immediately - wait for Apply button
  };

  const handleCancel = () => {
    // Reset temp selection to original selection
    setTempSelectedStores([...selectedStores]);
    setIsOpen(false);
    setSearchTerm("");
  };

  const handleApplyFilters = () => {
    // Apply the temporary selection
    onSelectionChange([...tempSelectedStores]);
    setIsOpen(false);
    setSearchTerm("");
  };

  // Display text logic
  const getDisplayText = () => {
    if (selectedStores.length === 0) {
      return "Select stores first";
    } else if (selectedStores.length === stores.length) {
      return "All Stores";
    } else if (selectedStores.length === 1) {
      const selectedStore = stores.find(
        (store) => store.id === selectedStores[0]
      );
      return selectedStore?.name || "Selected Store";
    } else {
      return `${selectedStores.length} Stores Selected`;
    }
  };

  //sm:tw-pl-4 sm:tw-pr-2 tw-w-full sm:tw-w-auto tw-min-w-0 sm:tw-h-8

  return (
    <div
      className={clsx("tw-relative", className)}
      ref={dropdownRef}
      data-testid={testId}
    >
      <button
        type="button"
        className={clsx(
          "tw-flex tw-h-8 sm:tw-h-9 tw-shrink-0 tw-items-center tw-justify-center tw-gap-x-2 tw-rounded-lg tw-px-3 sm:tw-pl-4 sm:tw-pr-2 tw-bg-slate-200/60 dark:tw-bg-slate-800/60 sm:tw-bg-white tw-border-0 sm:tw-border sm:tw-border-gray-300 ",
          {
            "tw-text-gray-400 tw-cursor-not-allowed": disabled,
            "hover:tw-bg-slate-300/60 dark:hover:tw-bg-slate-700/60 sm:hover:tw-bg-gray-50":
              !disabled,
          }
        )}
        onClick={handleToggleDropdown}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleToggleDropdown();
          } else if (e.key === "Escape" && isOpen) {
            e.preventDefault();
            setIsOpen(false);
          }
        }}
        disabled={disabled}
        data-testid="store-filter-button"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label={`Store filter, ${selectedStores.length} stores selected`}
      >
        <span className="material-symbols-outlined tw-text-sm tw-text-slate-600 dark:tw-text-slate-300 sm:tw-hidden">
          store
        </span>
        <p className="tw-text-slate-800 dark:tw-text-slate-200 sm:tw-text-gray-800 tw-text-xs sm:tw-text-sm tw-font-medium tw-leading-normal tw-truncate tw-min-w-0">
          <span className="sm:tw-inline tw-hidden">Store: </span>
          <span className="tw-font-bold tw-truncate">
            {selectedStores.length === 0 ? (
              placeholder === "All Stores" ? (
                "Select stores..."
              ) : (
                placeholder
              )
            ) : selectedStores.length === stores.length ? (
              "All Stores"
            ) : selectedStores.length === 1 ? (
              formatStoreDisplayName(
                stores.find((store) => store.id === selectedStores[0])!
              ) || "Selected Store"
            ) : selectedStores.length <= maxDisplayItems ? (
              selectedStores
                .map((storeId) => {
                  const store = stores.find((s) => s.id === storeId);
                  return store
                    ? formatStoreDisplayName(store)
                    : "Unknown Store";
                })
                .join(", ")
            ) : (
              <>
                <span className="sm:tw-hidden">
                  {selectedStores.length > 1
                    ? "Stores"
                    : formatStoreDisplayName(
                        stores.find((store) => store.id === selectedStores[0])!
                      ) || "Stores"}
                </span>
                <span className="tw-hidden sm:tw-inline">
                  {selectedStores.length} Stores Selected
                </span>
              </>
            )}
          </span>
        </p>
        {selectedStores.length > 0 && selectedStores.length < stores.length && (
          <span className="tw-inline-flex tw-items-center tw-justify-center tw-rounded-full tw-bg-primary tw-px-1.5 tw-py-0.5 tw-text-[10px] tw-font-bold tw-text-white sm:tw-hidden">
            {selectedStores.length}
          </span>
        )}
        <span
          className={clsx(
            "material-symbols-outlined tw-text-base tw-text-slate-500 dark:tw-text-slate-400 tw-transition-transform tw-shrink-0",
            { "tw-rotate-180": isOpen }
          )}
        >
          expand_more
        </span>
      </button>

      {isOpen && (
        <>
          {/* Mobile Overlay - Optimized Modal */}
          <div
            className="sm:tw-hidden tw-fixed tw-inset-0 tw-z-[120] tw-bg-black tw-bg-opacity-50"
            data-testid="store-filter-dropdown"
            role="listbox"
            aria-label="Store options"
          >
            <div className="tw-fixed tw-inset-x-0 tw-bottom-20 tw-bg-white tw-rounded-t-lg tw-max-h-[70vh] tw-flex tw-flex-col tw-shadow-xl">
              {/* Mobile Header */}
              <div className="tw-p-6 tw-border-b tw-border-gray-200 tw-shrink-0">
                <h2 className="tw-text-lg tw-font-semibold tw-text-gray-900 tw-text-center">
                  Select Stores
                </h2>
              </div>

              {/* Mobile Search */}
              <div className="tw-px-6 tw-py-4 tw-border-b tw-border-gray-200 tw-bg-gray-50 tw-shrink-0">
                <div className="tw-relative tw-flex tw-items-center">
                  <span className="material-symbols-outlined tw-absolute tw-left-3 tw-text-gray-400 tw-text-lg">
                    search
                  </span>
                  <input
                    className="tw-w-full tw-pl-10 tw-pr-4 tw-py-2.5 tw-text-sm tw-border tw-border-gray-300 tw-rounded-md focus:tw-ring-1 focus:tw-ring-primary focus:tw-border-primary placeholder:tw-text-gray-400 tw-bg-white"
                    placeholder="Search stores..."
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    data-testid="store-search-input"
                  />
                </div>
              </div>

              {/* Mobile Store List */}
              <div className="tw-overflow-y-auto tw-flex-1 tw-px-6 tw-py-4">
                {/* Mobile All Stores Option */}
                <label className="tw-flex tw-items-center tw-py-4 tw-cursor-pointer tw-border-b tw-border-gray-100">
                  <input
                    checked={allStoresSelected}
                    onChange={handleAllStoresToggle}
                    className="tw-h-5 tw-w-5 tw-text-primary tw-border-2 tw-border-gray-300 tw-rounded-md focus:tw-ring-primary tw-mr-4"
                    type="checkbox"
                  />
                  <div className="tw-flex tw-flex-col tw-flex-1">
                    <span className="tw-text-base tw-font-semibold tw-text-gray-900">
                      All Stores
                    </span>
                    <span className="tw-text-sm tw-text-gray-500 tw-mt-1">
                      Select all available stores
                    </span>
                  </div>
                </label>

                {filteredStores.map((store) => {
                  const isSelected = tempSelectedStores.includes(store.id);
                  const marketplaceInfo = getMarketplaceInfo(store);

                  return (
                    <label
                      key={store.id}
                      className={clsx(
                        "tw-flex tw-items-center tw-py-4 tw-cursor-pointer tw-border-b tw-border-gray-100 last:tw-border-b-0 tw-transition-colors",
                        {
                          "selected tw-bg-blue-50 tw-border-blue-200":
                            showSelectionStyle && isSelected,
                          "tw-bg-white": showSelectionStyle && !isSelected,
                          "hover:tw-bg-gray-50":
                            !showSelectionStyle || !isSelected,
                          "hover:tw-bg-blue-100":
                            showSelectionStyle && isSelected,
                        }
                      )}
                      role="option"
                      aria-selected={isSelected}
                    >
                      <input
                        checked={isSelected}
                        onChange={() => handleStoreToggle(store.id)}
                        className="tw-h-5 tw-w-5 tw-text-primary tw-border-2 tw-border-gray-300 tw-rounded-md focus:tw-ring-primary tw-mr-4"
                        type="checkbox"
                      />
                      <div className="tw-flex tw-flex-col tw-flex-1">
                        <span className="tw-text-base tw-font-medium tw-text-gray-900">
                          {formatStoreDisplayName(store)}
                        </span>
                        <span className="tw-text-sm tw-text-gray-500 tw-mt-1">
                          {store.marketplace
                            ? store.marketplace.charAt(0).toUpperCase() +
                              store.marketplace.slice(1)
                            : "Unknown"}
                        </span>
                      </div>
                      {marketplaceInfo.type === "image" ? (
                        <img
                          src={marketplaceInfo.src}
                          alt=""
                          className="tw-w-6 tw-h-6 tw-object-contain tw-flex-shrink-0"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            const parent = target.parentElement;
                            if (parent) {
                              const letter = store.name.charAt(0).toUpperCase();
                              const bgColor = getLetterBadgeColor(
                                store.marketplace || "unknown"
                              );
                              parent.innerHTML = `<span class="tw-text-white tw-text-xs tw-font-bold tw-px-2 tw-py-1 tw-rounded tw-w-6 tw-h-6 tw-flex tw-items-center tw-justify-center ${bgColor}">${letter}</span>`;
                            }
                          }}
                        />
                      ) : (
                        <span
                          className={clsx(
                            "tw-text-white tw-text-xs tw-font-bold tw-px-2 tw-py-1 tw-rounded tw-w-6 tw-h-6 tw-flex tw-items-center tw-justify-center",
                            marketplaceInfo.bgColor
                          )}
                        >
                          {marketplaceInfo.letter}
                        </span>
                      )}
                    </label>
                  );
                })}

                {filteredStores.length === 0 && (
                  <div className="tw-p-6 tw-text-center tw-text-gray-500 tw-text-sm">
                    No stores found matching "{searchTerm}"
                  </div>
                )}
              </div>

              {/* Mobile Footer Buttons - Enhanced visibility */}
              <div className="tw-p-4 tw-border-t tw-border-gray-200 tw-flex tw-gap-3 tw-shrink-0 tw-bg-white tw-shadow-lg tw-backdrop-blur-sm">
                <button
                  onClick={handleCancel}
                  className="tw-flex-1 tw-px-4 tw-py-3 tw-text-base tw-font-medium tw-text-gray-700 tw-bg-white tw-border tw-border-gray-300 tw-rounded-lg hover:tw-bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleApplyFilters}
                  className="tw-flex-1 tw-px-4 tw-py-3 tw-text-base tw-font-medium tw-text-white tw-bg-primary tw-rounded-lg hover:tw-bg-primary/90"
                >
                  Apply
                </button>
              </div>
            </div>
          </div>

          {/* Desktop Dropdown - Enhanced for better store visibility */}
          <div
            data-testid="store-filter-select-dropdown"
            className="tw-absolute tw-top-full tw-left-0 tw-mt-2 tw-w-80 tw-bg-white tw-rounded-lg tw-shadow-xl tw-border tw-border-gray-200 tw-z-[9999] tw-overflow-hidden tw-flex tw-flex-col tw-max-h-[500px] tw-block"
            role="listbox"
            aria-label="Store options"
          >
            {/* Desktop Header */}
            <div className="tw-p-4 tw-border-b tw-border-gray-100 tw-flex tw-items-center tw-justify-between tw-bg-white tw-shrink-0">
              <h3 className="tw-font-semibold tw-text-gray-900 tw-text-sm">
                Select Stores & Marketplaces
              </h3>
              <button
                className={clsx(
                  "tw-text-xs tw-font-medium",
                  selectedStores.length > 0
                    ? "tw-text-primary hover:tw-text-primary/80"
                    : "tw-text-gray-400 tw-cursor-not-allowed"
                )}
                onClick={selectedStores.length > 0 ? handleClearAll : undefined}
                disabled={selectedStores.length === 0}
                data-testid="clear-all-button"
              >
                Clear All
              </button>
            </div>

            {/* Desktop Search */}
            <div className="tw-px-3 tw-py-2 tw-border-b tw-border-gray-100 tw-bg-gray-50 tw-shrink-0">
              <div className="tw-relative tw-flex tw-items-center">
                <span className="material-symbols-outlined tw-absolute tw-left-2 tw-text-gray-400 tw-text-lg">
                  search
                </span>
                <input
                  className="tw-w-full tw-pl-8 tw-pr-3 tw-py-1.5 tw-text-sm tw-border tw-border-gray-300 tw-rounded-md focus:tw-ring-1 focus:tw-ring-primary focus:tw-border-primary placeholder:tw-text-gray-400 tw-bg-white"
                  placeholder="Search stores..."
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  data-testid="store-search-input"
                  ref={searchInputRef}
                />
              </div>
            </div>

            {/* Desktop Store List */}
            <div className="tw-overflow-y-auto tw-p-2 tw-flex-1 tw-min-h-0">
              {/* All Stores Option */}
              <label
                className="tw-flex tw-items-center tw-p-2 tw-rounded tw-mb-1 hover:tw-bg-gray-50 tw-cursor-pointer"
                data-testid="select-all-button"
              >
                <input
                  checked={allStoresSelected}
                  onChange={handleAllStoresToggle}
                  className="tw-h-4 tw-w-4 tw-text-primary tw-border-gray-300 tw-rounded focus:tw-ring-primary focus:tw-ring-2"
                  type="checkbox"
                />
                <span className="tw-ml-2.5 tw-text-sm tw-font-medium tw-text-gray-900">
                  All Stores
                </span>
              </label>

              <div className="tw-h-px tw-bg-gray-100 tw-my-1 tw-mx-2"></div>

              {/* Desktop Individual Store Options */}
              {filteredStores.map((store) => {
                const isSelected = tempSelectedStores.includes(store.id);
                const marketplaceInfo = getMarketplaceInfo(store);

                return (
                  <label
                    key={store.id}
                    className={clsx(
                      "tw-flex tw-items-center tw-p-2 tw-rounded tw-cursor-pointer tw-mb-0.5 tw-transition-colors",
                      {
                        "selected tw-bg-blue-50 tw-border-blue-200 tw-border":
                          showSelectionStyle && isSelected,
                        "tw-bg-white tw-border-gray-200 tw-border":
                          showSelectionStyle && !isSelected,
                        "hover:tw-bg-gray-50":
                          !showSelectionStyle || !isSelected,
                        "hover:tw-bg-blue-100":
                          showSelectionStyle && isSelected,
                      }
                    )}
                    data-testid={`store-option-${store.id}`}
                    role="option"
                    aria-selected={isSelected}
                  >
                    <input
                      checked={isSelected}
                      onChange={() => handleStoreToggle(store.id)}
                      className="tw-h-4 tw-w-4 tw-text-primary tw-border-gray-300 tw-rounded focus:tw-ring-primary focus:tw-ring-2"
                      type="checkbox"
                    />
                    <div className="tw-ml-2.5 tw-flex tw-items-center tw-gap-2">
                      {marketplaceInfo.type === "image" ? (
                        <img
                          src={marketplaceInfo.src}
                          alt=""
                          className="tw-w-4 tw-h-4 tw-object-contain tw-flex-shrink-0"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            const parent = target.parentElement;
                            if (parent) {
                              const letter = store.name.charAt(0).toUpperCase();
                              const bgColor = getLetterBadgeColor(
                                store.marketplace || "unknown"
                              );
                              parent.innerHTML = `<span class="tw-text-white tw-text-[10px] tw-font-bold tw-px-1 tw-rounded tw-w-4 tw-h-4 tw-flex tw-items-center tw-justify-center ${bgColor}">${letter}</span>`;
                            }
                          }}
                        />
                      ) : (
                        <span
                          className={clsx(
                            "tw-text-white tw-text-[10px] tw-font-bold tw-px-1 tw-rounded tw-w-4 tw-h-4 tw-flex tw-items-center tw-justify-center",
                            marketplaceInfo.bgColor
                          )}
                        >
                          {marketplaceInfo.letter}
                        </span>
                      )}
                      <div className="tw-flex tw-flex-col tw-gap-0.5">
                        <span className="tw-text-sm tw-text-gray-700">
                          {formatStoreDisplayName(store)}
                        </span>
                        {!store.isActive && (
                          <span className="tw-text-xs tw-text-red-500 tw-font-medium">
                            Inactive
                          </span>
                        )}
                      </div>
                    </div>
                  </label>
                );
              })}

              {filteredStores.length === 0 && (
                <div className="tw-p-4 tw-text-center tw-text-gray-500 tw-text-sm">
                  No stores found matching "{searchTerm}"
                </div>
              )}
            </div>

            {/* Desktop Footer - Enhanced visibility */}
            <div className="tw-p-3 tw-bg-gray-50 tw-border-t tw-border-gray-200 tw-flex tw-justify-end tw-gap-2 tw-shrink-0 tw-shadow-lg tw-backdrop-blur-sm">
              <button
                onClick={handleCancel}
                className="tw-px-3 tw-py-1.5 tw-text-sm tw-font-medium tw-text-gray-600 tw-bg-white tw-border tw-border-gray-300 tw-rounded-md hover:tw-bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleApplyFilters}
                className="tw-px-3 tw-py-1.5 tw-text-sm tw-font-medium tw-text-white tw-bg-primary tw-rounded-md hover:tw-bg-primary/90 tw-shadow-sm"
              >
                Apply Filters
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default StoreFilter;
